import type React from "react"
import { useState } from "react"
import Navbar from "../components/Navbar"
import Footer from "../components/Footer"
import Image from "../components/Image"
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageSquare,
  Headphones,
  Wrench,
  FileText,
  Users,
  Globe,
  Send,
  CheckCircle,
} from "lucide-react"

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    inquiryType: "",
    subject: "",
    message: "",
    preferredContact: "email",
    newsletter: false,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 2000))

    setIsSubmitting(false)
    setIsSubmitted(true)

    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false)
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        inquiryType: "",
        subject: "",
        message: "",
        preferredContact: "email",
        newsletter: false,
      })
    }, 3000)
  }

  const contactMethods = [
    {
      icon: Phone,
      title: "Phone Support",
      description: "Speak directly with our experts",
      details: "+61 3 1234 5678",
      hours: "Mon-Fri: 8:00 AM - 6:00 PM AEST",
      action: "Call Now",
    },
    {
      icon: Mail,
      title: "Email Support",
      description: "Get detailed responses to your inquiries",
      details: "info@mpv-multihog.com.au",
      hours: "Response within 24 hours",
      action: "Send Email",
    },
    {
      icon: MessageSquare,
      title: "Live Chat",
      description: "Instant support during business hours",
      details: "Available on website",
      hours: "Mon-Fri: 9:00 AM - 5:00 PM AEST",
      action: "Start Chat",
    },
    {
      icon: Headphones,
      title: "Technical Support",
      description: "Expert technical assistance",
      details: "support@mpv-multihog.com.au",
      hours: "24/7 Emergency Support Available",
      action: "Get Help",
    },
  ]

  const offices = [
    {
      name: "Melbourne Headquarters",
      type: "Main Office & Manufacturing",
      address: "123 Industrial Drive, Melbourne, VIC 3000",
      phone: "+61 3 1234 5678",
      email: "melbourne@mpv-multihog.com.au",
      hours: "Mon-Fri: 8:00 AM - 6:00 PM",
      services: ["Sales", "Manufacturing", "R&D", "Training", "Parts"],
      image: "https://placehold.co/400x300?text=Melbourne+HQ",
    },
    {
      name: "Sydney Service Center",
      type: "Sales & Service",
      address: "456 Business Park Road, Sydney, NSW 2000",
      phone: "+61 2 9876 5432",
      email: "sydney@mpv-multihog.com.au",
      hours: "Mon-Fri: 8:00 AM - 5:00 PM",
      services: ["Sales", "Service", "Parts", "Training"],
      image: "https://placehold.co/400x300?text=Sydney+Office",
    },
    {
      name: "Brisbane Regional Office",
      type: "Sales & Support",
      address: "789 Commerce Street, Brisbane, QLD 4000",
      phone: "+61 7 3456 7890",
      email: "brisbane@mpv-multihog.com.au",
      hours: "Mon-Fri: 8:00 AM - 5:00 PM",
      services: ["Sales", "Service", "Parts"],
      image: "https://placehold.co/400x300?text=Brisbane+Office",
    },
    {
      name: "Perth Service Hub",
      type: "Service & Parts",
      address: "321 Mining District Ave, Perth, WA 6000",
      phone: "+61 8 9123 4567",
      email: "perth@mpv-multihog.com.au",
      hours: "Mon-Fri: 8:00 AM - 5:00 PM",
      services: ["Service", "Parts", "Mining Support"],
      image: "https://placehold.co/400x300?text=Perth+Hub",
    },
  ]

  const supportServices = [
    {
      icon: Wrench,
      title: "Technical Support",
      description: "Expert technical assistance for all Multihog models and attachments",
      features: ["Remote diagnostics", "On-site service", "Preventive maintenance", "Emergency repairs"],
    },
    {
      icon: FileText,
      title: "Documentation",
      description: "Comprehensive manuals, guides, and technical documentation",
      features: ["Operation manuals", "Maintenance guides", "Parts catalogs", "Safety documentation"],
    },
    {
      icon: Users,
      title: "Training Programs",
      description: "Professional training for operators and maintenance personnel",
      features: ["Operator certification", "Maintenance training", "Safety programs", "Custom workshops"],
    },
    {
      icon: Globe,
      title: "Parts & Service",
      description: "Genuine parts and professional service support nationwide",
      features: ["Genuine parts", "Express delivery", "Service scheduling", "Warranty support"],
    },
  ]

  const inquiryTypes = [
    "General Inquiry",
    "Product Information",
    "Quote Request",
    "Technical Support",
    "Service Request",
    "Parts Order",
    "Training Information",
    "Partnership Opportunity",
    "Media Inquiry",
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              Get in Touch
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-red-500">Contact</span> Our
              <br />
              Expert Team
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Ready to transform your operations with Multihog solutions? Our expert team is here to help you find the
              perfect equipment for your needs and provide ongoing support.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">How Can We Help You?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose the contact method that works best for you. Our team is ready to assist with any questions or
                requirements.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {contactMethods.map((method, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow text-center"
                >
                  <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <method.icon className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{method.title}</h3>
                  <p className="text-gray-600 mb-4">{method.description}</p>
                  <div className="mb-4">
                    <p className="font-semibold text-gray-900">{method.details}</p>
                    <p className="text-sm text-gray-500">{method.hours}</p>
                  </div>
                  <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors w-full">
                    {method.action}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>

                {isSubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent Successfully!</h3>
                    <p className="text-gray-600">
                      Thank you for your inquiry. Our team will get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        Company/Organization
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="inquiryType" className="block text-sm font-medium text-gray-700 mb-2">
                        Inquiry Type *
                      </label>
                      <select
                        id="inquiryType"
                        name="inquiryType"
                        required
                        value={formData.inquiryType}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="">Select inquiry type</option>
                        {inquiryTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Please provide details about your inquiry..."
                      ></textarea>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">Preferred Contact Method</label>
                      <div className="flex gap-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="preferredContact"
                            value="email"
                            checked={formData.preferredContact === "email"}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          Email
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="preferredContact"
                            value="phone"
                            checked={formData.preferredContact === "phone"}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          Phone
                        </label>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="newsletter"
                        name="newsletter"
                        checked={formData.newsletter}
                        onChange={handleInputChange}
                        className="mr-3"
                      />
                      <label htmlFor="newsletter" className="text-sm text-gray-700">
                        Subscribe to our newsletter for product updates and industry insights
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Quick Contact Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Phone className="h-5 w-5 text-red-600 mr-3" />
                      <div>
                        <p className="font-semibold text-gray-900">Main Office</p>
                        <p className="text-gray-600">+61 3 1234 5678</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 text-red-600 mr-3" />
                      <div>
                        <p className="font-semibold text-gray-900">General Inquiries</p>
                        <p className="text-gray-600">info@mpv-multihog.com.au</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <MapPin className="h-5 w-5 text-red-600 mr-3 mt-1" />
                      <div>
                        <p className="font-semibold text-gray-900">Address</p>
                        <p className="text-gray-600">
                          123 Industrial Drive
                          <br />
                          Melbourne, VIC 3000
                          <br />
                          Australia
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-5 w-5 text-red-600 mr-3" />
                      <div>
                        <p className="font-semibold text-gray-900">Business Hours</p>
                        <p className="text-gray-600">Mon-Fri: 8:00 AM - 6:00 PM AEST</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-red-600 text-white rounded-xl p-8">
                  <h3 className="text-xl font-bold mb-4">Emergency Support</h3>
                  <p className="mb-4">
                    Need urgent technical support? Our emergency hotline is available 24/7 for critical situations.
                  </p>
                  <div className="flex items-center mb-4">
                    <Phone className="h-5 w-5 mr-2" />
                    <span className="font-semibold">+61 1800 MPV HELP</span>
                  </div>
                  <p className="text-red-100 text-sm">
                    Emergency support charges may apply for after-hours service calls.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Locations</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                With offices and service centers across Australia, we're always close by to provide support when you
                need it.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {offices.map((office, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <Image
                    src={office.image || "/placeholder.svg"}
                    alt={office.name}
                    className="w-full h-48 object-cover"
                    width={400}
                    height={300}
                  />
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{office.name}</h3>
                        <p className="text-red-600 font-medium">{office.type}</p>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-start">
                        <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                        <p className="text-gray-600">{office.address}</p>
                      </div>
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 text-gray-400 mr-2" />
                        <p className="text-gray-600">{office.phone}</p>
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-gray-400 mr-2" />
                        <p className="text-gray-600">{office.email}</p>
                      </div>
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 text-gray-400 mr-2" />
                        <p className="text-gray-600">{office.hours}</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Services Available:</h4>
                      <div className="flex flex-wrap gap-2">
                        {office.services.map((service, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Support Services */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Support Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive support services to keep your Multihog equipment operating at peak performance.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {supportServices.map((service, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="bg-red-100 rounded-full p-3 w-12 h-12 mb-4 flex items-center justify-center">
                    <service.icon className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-red-600 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600">Quick answers to common questions about our products and services</p>
            </div>

            <div className="space-y-6">
              {[
                {
                  question: "What is the typical response time for support requests?",
                  answer:
                    "We respond to all support requests within 24 hours during business days. Emergency support is available 24/7 with immediate response for critical situations.",
                },
                {
                  question: "Do you provide training for new equipment?",
                  answer:
                    "Yes, we provide comprehensive training programs for all operators and maintenance personnel. Training can be conducted at our facilities or on-site at your location.",
                },
                {
                  question: "What warranty coverage is included with Multihog equipment?",
                  answer:
                    "All Multihog tractors come with a comprehensive 2-year warranty covering parts and labor. Extended warranty options are available for additional coverage.",
                },
                {
                  question: "Can you help with financing options?",
                  answer:
                    "Yes, we work with various financing partners to provide flexible payment options including leasing, rental programs, and customized financing solutions.",
                },
                {
                  question: "How quickly can you deliver equipment?",
                  answer:
                    "Standard models are typically available within 4-6 weeks. Custom configurations may require 8-12 weeks depending on specifications and current demand.",
                },
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default ContactPage
